// src/pages/BillingSystem.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import nooLogo from '../assets/noo.png';
import leftArrowIcon from '../assets/left.png';

const displayFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };
const bodyFont = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" };
const monoFont = { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace" };

const paymentStatusStyles = {
  Paid: 'bg-[#4FB3A9]/15 text-[#0F3D3E]',
  Pending: 'bg-[#D6A419]/20 text-[#8a6b13]',
  Partial: 'bg-orange-100 text-orange-700',
};

const paymentStatusDot = {
  Paid: 'bg-[#4FB3A9]',
  Pending: 'bg-[#D6A419]',
  Partial: 'bg-orange-500',
};

const BillingSystem = () => {
  const mockServices = [
    { id: 'serv1', name: 'Consultation', price: 300 },
    { id: 'serv2', name: 'Endoscopy', price: 1500 },
    { id: 'serv3', name: 'Biopsy', price: 800 },
    { id: 'serv4', name: 'Lab Tests', price: 500 },
    { id: 'serv5', name: 'Follow-up Visit', price: 200 },
  ];

  const mockPatients = [
    { id: 'pat001', name: 'Nour Ali' },
    { id: 'pat002', name: 'Fatma Khaled' },
    { id: 'pat003', name: 'Mohamed Tarek' },
  ];

  const [invoices, setInvoices] = useState(() => {
    const savedInvoices = localStorage.getItem('clinicInvoices');
    return savedInvoices ? JSON.parse(savedInvoices) : [];
  });

  useEffect(() => {
    localStorage.setItem('clinicInvoices', JSON.stringify(invoices));
  }, [invoices]);

  const [showModal, setShowModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);

  const [formPatientId, setFormPatientId] = useState('');
  const [formServices, setFormServices] = useState([{ serviceId: '', quantity: 1, price: 0 }]);
  const [formPaymentStatus, setFormPaymentStatus] = useState('Pending');
  const [formPaymentDate, setFormPaymentDate] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPatientId, setFilterPatientId] = useState('All');

  const calculateTotal = (services) => {
    return services.reduce((total, item) => total + (item.quantity * item.price), 0);
  };

  const handleAddInvoice = () => {
    setCurrentInvoice(null);
    setFormPatientId('');
    setFormServices([{ serviceId: '', quantity: 1, price: 0 }]);
    setFormPaymentStatus('Pending');
    setFormPaymentDate('');
    setFormNotes('');
    setShowModal(true);
  };

  const handleEditInvoice = (invoice) => {
    setCurrentInvoice(invoice);
    setFormPatientId(invoice.patientId);
    setFormServices(invoice.services);
    setFormPaymentStatus(invoice.paymentStatus);
    setFormPaymentDate(invoice.paymentDate || '');
    setFormNotes(invoice.notes || '');
    setShowModal(true);
  };

  const handleAddServiceToForm = () => {
    setFormServices([...formServices, { serviceId: '', quantity: 1, price: 0 }]);
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...formServices];
    if (field === 'serviceId') {
      const selectedService = mockServices.find(s => s.id === value);
      updatedServices[index] = {
        ...updatedServices[index],
        serviceId: value,
        price: selectedService ? selectedService.price : 0,
      };
    } else {
      updatedServices[index] = { ...updatedServices[index], [field]: value };
    }
    setFormServices(updatedServices);
  };

  const handleRemoveServiceFromForm = (index) => {
    const updatedServices = formServices.filter((_, i) => i !== index);
    setFormServices(updatedServices);
  };

  const handleSubmitInvoice = (e) => {
    e.preventDefault();
    const patient = mockPatients.find(p => p.id === formPatientId);
    if (!patient) {
      alert('Please select a valid patient.');
      return;
    }

    const newInvoice = {
      id: currentInvoice ? currentInvoice.id : `inv${Date.now()}`,
      patientId: formPatientId,
      patientName: patient.name,
      date: currentInvoice ? currentInvoice.date : new Date().toISOString().split('T')[0],
      services: formServices.map(s => ({
        ...s,
        serviceName: mockServices.find(ms => ms.id === s.serviceId)?.name || 'Unknown Service'
      })),
      totalAmount: calculateTotal(formServices),
      paymentStatus: formPaymentStatus,
      paymentDate: formPaymentDate,
      notes: formNotes,
    };

    if (currentInvoice) {
      setInvoices(invoices.map(inv => inv.id === newInvoice.id ? newInvoice : inv));
    } else {
      setInvoices([newInvoice, ...invoices]);
    }
    setShowModal(false);
  };

  const handleDeleteInvoice = (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      setInvoices(invoices.filter(inv => inv.id !== id));
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const statusMatch = filterStatus === 'All' || invoice.paymentStatus === filterStatus;
    const patientMatch = filterPatientId === 'All' || invoice.patientId === filterPatientId;
    return statusMatch && patientMatch;
  });

  const totalOutstanding = invoices
    .filter(inv => inv.paymentStatus !== 'Paid')
    .reduce((sum, inv) => sum + inv.totalAmount, 0);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" style={bodyFont}>
      {/* Header */}
      <div className="bg-white border-b border-[#E4E1D8]">
        <div className="container mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/home">
              <img src={nooLogo} alt="Drogastro" className="w-12 h-12 object-contain" />
            </Link>
            <div>
              <p className="text-[11px] tracking-wider text-[#0F3D3E]/50" style={monoFont}>FINANCE</p>
              <h1 className="text-xl md:text-2xl font-bold text-[#0F3D3E]" style={displayFont}>Billing &amp; Accounting</h1>
            </div>
          </div>
          <Link
            to="/home"
            className="flex items-center gap-2 bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#0F3D3E] font-semibold py-2 px-4 rounded-md border border-[#E4E1D8] transition-colors duration-200 text-sm"
          >
            <img src={leftArrowIcon} alt="Back" className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>TOTAL INVOICES</p>
            <p className="text-2xl font-bold text-[#0F3D3E]" style={displayFont}>{invoices.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>OUTSTANDING</p>
            <p className="text-2xl font-bold text-[#D6A419]" style={displayFont}>{totalOutstanding.toFixed(2)} EGP</p>
          </div>
          <div className="hidden md:block bg-white rounded-xl border border-[#E4E1D8] p-4">
            <p className="text-[11px] tracking-wider text-[#0F3D3E]/50 mb-1" style={monoFont}>PAID INVOICES</p>
            <p className="text-2xl font-bold text-[#4FB3A9]" style={displayFont}>
              {invoices.filter(inv => inv.paymentStatus === 'Paid').length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-sm p-5 md:p-8">
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-6 gap-4">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <label htmlFor="filter-status" className="text-xs font-semibold text-[#0F3D3E]/70 whitespace-nowrap" style={monoFont}>STATUS</label>
              <select
                id="filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="p-2 border border-[#E4E1D8] rounded-md w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Partial">Partial</option>
              </select>

              <label htmlFor="filter-patient" className="text-xs font-semibold text-[#0F3D3E]/70 whitespace-nowrap" style={monoFont}>PATIENT</label>
              <select
                id="filter-patient"
                value={filterPatientId}
                onChange={(e) => setFilterPatientId(e.target.value)}
                className="p-2 border border-[#E4E1D8] rounded-md w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
              >
                <option value="All">All Patients</option>
                {mockPatients.map(patient => (
                  <option key={patient.id} value={patient.id}>{patient.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleAddInvoice}
              className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200 w-full md:w-auto"
            >
              + Create New Invoice
            </button>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-[#0F3D3E]" style={displayFont}>All Invoices</h2>
            <span className="text-xs font-semibold text-[#0F3D3E]/50 bg-[#FAF9F6] border border-[#E4E1D8] rounded-full px-3 py-1" style={monoFont}>
              {filteredInvoices.length} SHOWN
            </span>
          </div>

          {filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-14 px-6 bg-[#FAF9F6] rounded-lg border border-dashed border-[#E4E1D8]">
              <p className="text-[#22262B]/60 text-base mb-1">No invoices found matching the criteria.</p>
              <button onClick={handleAddInvoice} className="text-[#4FB3A9] font-semibold text-sm hover:underline mt-1">
                Create the first one
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-[#E4E1D8]">
              <table className="min-w-full bg-white">
                <thead className="bg-[#0F3D3E]/5">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Invoice ID</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Date</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Patient Name</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Total Amount</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Status</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-[#0F3D3E] uppercase tracking-wider" style={monoFont}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(invoice => (
                    <tr key={invoice.id} className="border-t border-[#E4E1D8] hover:bg-[#FAF9F6] transition-colors">
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]/70" style={monoFont}>{invoice.id}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B]">{invoice.date}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#22262B] font-medium">{invoice.patientName}</td>
                      <td className="py-3 px-4 whitespace-nowrap text-[#0F3D3E] font-semibold">{invoice.totalAmount.toFixed(2)} EGP</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${paymentStatusStyles[invoice.paymentStatus] || 'bg-gray-200 text-gray-800'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${paymentStatusDot[invoice.paymentStatus] || 'bg-gray-500'}`} />
                          {invoice.paymentStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <button onClick={() => handleEditInvoice(invoice)} className="text-[#0F3D3E] hover:text-[#4FB3A9] font-medium mr-4 transition-colors">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteInvoice(invoice.id)} className="text-red-600 hover:text-red-800 font-medium transition-colors">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-[#0F3D3E]/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl border border-[#E4E1D8] shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-8 pt-7 pb-5 border-b border-[#E4E1D8] sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-[#0F3D3E]" style={displayFont}>
                {currentInvoice ? 'Edit Invoice' : 'Create New Invoice'}
              </h2>
            </div>
            <form onSubmit={handleSubmitInvoice} className="space-y-5 px-8 py-6">
              <div>
                <label htmlFor="modal-patient-select" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>PATIENT</label>
                <select
                  id="modal-patient-select"
                  value={formPatientId}
                  onChange={(e) => setFormPatientId(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  required
                >
                  <option value="">Select Patient</option>
                  {mockPatients.map(patient => (
                    <option key={patient.id} value={patient.id}>{patient.name} (ID: {patient.id})</option>
                  ))}
                </select>
              </div>

              {/* Services List */}
              <div className="border border-[#E4E1D8] rounded-lg bg-[#FAF9F6] overflow-hidden">
                <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#0F3D3E] tracking-wide" style={monoFont}>SERVICES</h3>
                  <button
                    type="button"
                    onClick={handleAddServiceToForm}
                    className="text-[#4FB3A9] hover:text-[#0F3D3E] font-semibold text-sm transition-colors"
                  >
                    + Add Service
                  </button>
                </div>

                <div className="px-4 pb-4 space-y-2">
                  {formServices.map((service, index) => (
                    <div key={index} className="bg-white border border-[#E4E1D8] rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <select
                        value={service.serviceId}
                        onChange={(e) => handleServiceChange(index, 'serviceId', e.target.value)}
                        className="flex-1 p-2 border border-[#E4E1D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                        required
                      >
                        <option value="">Select Service</option>
                        {mockServices.map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.price} EGP)</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2 shrink-0">
                        <input
                          type="number"
                          value={service.quantity}
                          onChange={(e) => handleServiceChange(index, 'quantity', parseInt(e.target.value) || 0)}
                          min="1"
                          className="w-16 p-2 border border-[#E4E1D8] rounded-md text-center text-sm focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                          required
                        />
                        <span className="text-xs text-[#22262B]/60 whitespace-nowrap">× {service.price.toFixed(2)}</span>
                        <span className="text-sm text-[#0F3D3E] font-semibold whitespace-nowrap w-24 text-right">
                          {(service.quantity * service.price).toFixed(2)} EGP
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveServiceFromForm(index)}
                          className="text-red-500 hover:text-red-700 p-1.5 rounded-md transition-colors"
                          aria-label="Remove service"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end items-center gap-2 px-4 py-3 bg-[#0F3D3E]/5 border-t border-[#E4E1D8]">
                  <span className="text-sm font-semibold text-[#0F3D3E]/70">Total:</span>
                  <span className="text-xl font-bold text-[#D6A419]" style={displayFont}>
                    {calculateTotal(formServices).toFixed(2)} EGP
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="modal-payment-status" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>PAYMENT STATUS</label>
                  <select
                    id="modal-payment-status"
                    value={formPaymentStatus}
                    onChange={(e) => setFormPaymentStatus(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                    required
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Partial">Partial</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="modal-payment-date" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>PAYMENT DATE</label>
                  <input
                    type="date"
                    id="modal-payment-date"
                    value={formPaymentDate}
                    onChange={(e) => setFormPaymentDate(e.target.value)}
                    className="w-full p-2.5 border border-[#E4E1D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="modal-notes" className="block text-xs font-semibold text-[#0F3D3E]/70 mb-1.5" style={monoFont}>NOTES</label>
                <textarea
                  id="modal-notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full p-2.5 border border-[#E4E1D8] rounded-md h-24 resize-none focus:outline-none focus:ring-2 focus:ring-[#4FB3A9] transition-shadow"
                  placeholder="Any additional notes for the invoice..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-[#FAF9F6] hover:bg-[#E4E1D8] text-[#22262B] font-semibold py-2.5 px-5 rounded-md border border-[#E4E1D8] transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#0F3D3E] hover:bg-[#0F3D3E]/90 text-white font-semibold py-2.5 px-5 rounded-md shadow-sm transition-colors duration-200"
                >
                  {currentInvoice ? 'Save Changes' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSystem;
